/**
 * ConfidenceCheck — Pre-simulation self-assessment overlay
 * Addresses exam anxiety (Prüfungsangst) with:
 * 1. Emoji-based confidence self-assessment
 * 2. Breathing exercise or tips based on selection
 * 3. Confidence tracking over time
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Play,
  Wind,
  Lightbulb,
  Sparkles,
  TrendingUp,
  Snowflake,
  Meh,
  Smile,
  BicepsFlexed,
} from 'lucide-react';

// ── Confidence History ──────────────────────────────────────
const CONFIDENCE_KEY = 'fahryo_confidence_history';

interface ConfidenceEntry {
  date: string;
  level: 1 | 2 | 3;
}

function saveConfidence(level: 1 | 2 | 3): void {
  try {
    const stored = localStorage.getItem(CONFIDENCE_KEY);
    const history: ConfidenceEntry[] = stored ? JSON.parse(stored) : [];
    history.push({ date: new Date().toISOString(), level });
    // Keep last 50
    const trimmed = history.slice(-50);
    localStorage.setItem(CONFIDENCE_KEY, JSON.stringify(trimmed));
  } catch {}
}

export function getConfidenceHistory(): ConfidenceEntry[] {
  try {
    const stored = localStorage.getItem(CONFIDENCE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function getConfidenceTrend(): string {
  const history = getConfidenceHistory();
  if (history.length < 2) return '';
  const recent = history.slice(-5);
  const emojis = recent.map((h) => (h.level === 1 ? '🥶' : h.level === 2 ? '😐' : '😎'));
  return emojis.join(' → ');
}

// ── Breathing Timer ──────────────────────────────────────────
const BreathingExercise: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [countdown, setCountdown] = useState(4);
  const [cycle, setCycle] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setPhase((p) => {
            if (p === 'inhale') return 'hold';
            if (p === 'hold') return 'exhale';
            setCycle((c) => c + 1);
            return 'inhale';
          });
          return 4;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  // After 2 full cycles, show "done"
  const isDone = cycle >= 2;

  const phaseConfig = {
    inhale: {
      label: t('confidence_breathe_in') || 'Einatmen...',
      color: 'from-blue-400 to-cyan-400',
      scale: 'scale-110',
    },
    hold: {
      label: t('confidence_breathe_hold') || 'Halten...',
      color: 'from-purple-400 to-indigo-400',
      scale: 'scale-110',
    },
    exhale: {
      label: t('confidence_breathe_out') || 'Ausatmen...',
      color: 'from-green-400 to-emerald-400',
      scale: 'scale-90',
    },
  };

  if (isDone) {
    return (
      <div className="text-center space-y-4 animate-in fade-in duration-500">
        <Sparkles size={40} className="mx-auto text-amber-400" />
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('confidence_encourage')}</p>
        <button
          onClick={onDone}
          className="w-full bg-gradient-to-r from-primary to-[var(--f-brand-gradient-end)] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <Play size={18} className="fill-white" />
          {t('confidence_start')}
        </button>
      </div>
    );
  }

  const current = phaseConfig[phase];

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">
        {t('confidence_breathing_title')}
      </div>

      {/* Animated circle */}
      <div
        className={`w-32 h-32 rounded-full bg-gradient-to-br ${current.color} flex items-center justify-center transition-transform duration-[4000ms] ease-in-out ${current.scale} shadow-2xl`}
      >
        <div className="text-center text-white">
          <div className="text-3xl font-black">{countdown}</div>
        </div>
      </div>

      <div className="text-sm font-bold text-gray-600 dark:text-gray-400 animate-pulse">
        {current.label}
      </div>

      <button
        onClick={onDone}
        className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
      >
        {t('confidence_skip') || 'Überspringen'}
      </button>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────
interface ConfidenceCheckProps {
  onStart: () => void;
  onClose: () => void;
}

const ConfidenceCheck: React.FC<ConfidenceCheckProps> = ({ onStart, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'assess' | 'action'>('assess');
  const [selectedLevel, setSelectedLevel] = useState<1 | 2 | 3 | null>(null);
  const trend = getConfidenceTrend();

  const handleSelect = (level: 1 | 2 | 3) => {
    setSelectedLevel(level);
    saveConfidence(level);

    if (level === 3) {
      // Confident → go directly
      setTimeout(() => onStart(), 600);
    } else {
      setStep('action');
    }
  };

  const levels = [
    { level: 1 as const, Icon: Snowflake, color: 'text-blue-400', label: t('confidence_level_1') },
    { level: 2 as const, Icon: Meh, color: 'text-amber-400', label: t('confidence_level_2') },
    { level: 3 as const, Icon: Smile, color: 'text-green-500', label: t('confidence_level_3') },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confidence-check-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div
        className="glass-strong relative w-full max-w-md mx-auto animate-in slide-in-from-bottom-8 duration-500 overflow-hidden max-h-[85vh]"
        style={{ borderRadius: '1.5rem 1.5rem 1.25rem 1.25rem' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 glass-pill flex items-center justify-center hover:opacity-80 transition-opacity z-10"
        >
          <X size={16} className="text-gray-500" />
        </button>

        <div className="p-6 pt-8">
          {step === 'assess' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Header */}
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center mb-4">
                  <Wind size={32} className="text-purple-500" />
                </div>
                <h2
                  id="confidence-check-title"
                  className="text-xl font-black text-gray-900 dark:text-white mb-2"
                >
                  {t('confidence_check_title')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('confidence_check_subtitle') || 'Bevor es losgeht — wie fühlst du dich?'}
                </p>
              </div>

              {/* Emoji Selection */}
              <div className="flex gap-3 justify-center">
                {levels.map((item) => (
                  <button
                    key={item.level}
                    onClick={() => handleSelect(item.level)}
                    className={`glass-card flex-1 flex flex-col items-center gap-2 p-4 transition-all duration-300 active:scale-95 ${
                      selectedLevel === item.level
                        ? 'border-primary scale-105 shadow-lg'
                        : 'hover:opacity-80'
                    }`}
                  >
                    <item.Icon size={36} className={item.color} strokeWidth={2.25} />
                    <span className="text-xs font-bold text-gray-600 dark:text-gray-400">
                      {item.label}
                    </span>
                  </button>
                ))}
              </div>

              {/* Confidence Trend */}
              {trend && (
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                  <TrendingUp size={12} />
                  <span>
                    {t('confidence_trend_title')}: {trend}
                  </span>
                </div>
              )}
            </div>
          )}

          {step === 'action' && selectedLevel === 1 && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              <BreathingExercise onDone={onStart} />
            </div>
          )}

          {step === 'action' && selectedLevel === 2 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
              {/* Tip */}
              <div className="text-center">
                <div className="w-14 h-14 mx-auto bg-amber-50 dark:bg-amber-900/20 rounded-2xl flex items-center justify-center mb-3">
                  <Lightbulb size={28} className="text-amber-500" />
                </div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white mb-2">
                  {t('confidence_tip_title')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t('confidence_tip_desc')}
                </p>
              </div>

              {/* Quick tips */}
              <div className="space-y-2">
                {[
                  t('confidence_tip_1') || 'Lies jede Frage zweimal durch.',
                  t('confidence_tip_2') || 'Achte auf Wörter wie "muss" vs. "darf".',
                  t('confidence_tip_3') || 'Bei Unsicherheit: Ausschlussverfahren nutzen.',
                ].map((tip, i) => (
                  <div key={i} className="glass-card flex items-start gap-2 p-3">
                    <Sparkles size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-gray-700 dark:text-gray-300">{tip}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={onStart}
                className="w-full bg-gradient-to-r from-primary to-[var(--f-brand-gradient-end)] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                <Play size={18} className="fill-white" />
                {t('confidence_start')}
              </button>
            </div>
          )}

          {/* Level 3 selected → brief animation */}
          {selectedLevel === 3 && (
            <div className="text-center py-6 animate-in fade-in zoom-in duration-500">
              <BicepsFlexed size={56} className="mx-auto mb-3 text-primary" />
              <p className="text-lg font-black text-gray-900 dark:text-white">
                {t('confidence_ready_desc')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfidenceCheck;
